import { denormalisedResponseEntities, ensureOwnListing } from '../util/data';
import { storableError } from '../util/errors';
import { LISTING_STATE_DRAFT } from '../util/types';
import * as log from '../util/log';
import { saveLanguagePreference, restoreProfileLanguage } from '../util/languagePreference';
import { getTransitionsNeedingProviderAttention } from '../transactions/transaction';

import { authInfo } from './auth.duck';
import { stripeAccountCreateSuccess } from './stripeConnectAccount.duck';
import { util as sdkUtil } from '../util/sdkLoader';

// ================ Action types ================ //

export const CURRENT_USER_SHOW_REQUEST = 'app/user/CURRENT_USER_SHOW_REQUEST';
export const CURRENT_USER_SHOW_SUCCESS = 'app/user/CURRENT_USER_SHOW_SUCCESS';
export const CURRENT_USER_SHOW_ERROR = 'app/user/CURRENT_USER_SHOW_ERROR';

export const CLEAR_CURRENT_USER = 'app/user/CLEAR_CURRENT_USER';

export const FETCH_CURRENT_USER_HAS_LISTINGS_REQUEST =
  'app/user/FETCH_CURRENT_USER_HAS_LISTINGS_REQUEST';
export const FETCH_CURRENT_USER_HAS_LISTINGS_SUCCESS =
  'app/user/FETCH_CURRENT_USER_HAS_LISTINGS_SUCCESS';
export const FETCH_CURRENT_USER_HAS_LISTINGS_ERROR =
  'app/user/FETCH_CURRENT_USER_HAS_LISTINGS_ERROR';

export const FETCH_CURRENT_USER_NOTIFICATIONS_REQUEST =
  'app/user/FETCH_CURRENT_USER_NOTIFICATIONS_REQUEST';
export const FETCH_CURRENT_USER_NOTIFICATIONS_SUCCESS =
  'app/user/FETCH_CURRENT_USER_NOTIFICATIONS_SUCCESS';
export const FETCH_CURRENT_USER_NOTIFICATIONS_ERROR =
  'app/user/FETCH_CURRENT_USER_NOTIFICATIONS_ERROR';

export const FETCH_CURRENT_USER_HAS_ORDERS_REQUEST =
  'app/user/FETCH_CURRENT_USER_HAS_ORDERS_REQUEST';
export const FETCH_CURRENT_USER_HAS_ORDERS_SUCCESS =
  'app/user/FETCH_CURRENT_USER_HAS_ORDERS_SUCCESS';
export const FETCH_CURRENT_USER_HAS_ORDERS_ERROR = 'app/user/FETCH_CURRENT_USER_HAS_ORDERS_ERROR';

export const SEND_VERIFICATION_EMAIL_REQUEST = 'app/user/SEND_VERIFICATION_EMAIL_REQUEST';
export const SEND_VERIFICATION_EMAIL_SUCCESS = 'app/user/SEND_VERIFICATION_EMAIL_SUCCESS';
export const SEND_VERIFICATION_EMAIL_ERROR = 'app/user/SEND_VERIFICATION_EMAIL_ERROR';

export const UPDATE_PROFILE_LANGUAGES_REQUEST = 'app/user/UPDATE_PROFILE_LANGUAGES_REQUEST';
export const UPDATE_PROFILE_LANGUAGES_SUCCESS = 'app/user/UPDATE_PROFILE_LANGUAGES_SUCCESS';
export const UPDATE_PROFILE_LANGUAGES_ERROR = 'app/user/UPDATE_PROFILE_LANGUAGES_ERROR';

export const SAVE_SHIPPING_ADDRESS_REQUEST = 'app/user/SAVE_SHIPPING_ADDRESS_REQUEST';
export const SAVE_SHIPPING_ADDRESS_SUCCESS = 'app/user/SAVE_SHIPPING_ADDRESS_SUCCESS';
export const SAVE_SHIPPING_ADDRESS_ERROR = 'app/user/SAVE_SHIPPING_ADDRESS_ERROR';

export const FETCH_COMMISSION_REQUEST = 'app/user/FETCH_COMMISSION_REQUEST';
export const FETCH_COMMISSION_SUCCESS = 'app/user/FETCH_COMMISSION_SUCCESS';
export const FETCH_COMMISSION_ERROR = 'app/user/FETCH_COMMISSION_ERROR';

export const DELETE_ADDRESS_REQUEST = 'app/user/DELETE_ADDRESS_REQUEST';
export const DELETE_ADDRESS_SUCCESS = 'app/user/DELETE_ADDRESS_SUCCESS';
export const DELETE_ADDRESS_ERROR = 'app/user/DELETE_ADDRESS_ERROR';

export const SHOW_EMAIL_VERIFICATION_POPUP = 'app/user/SHOW_EMAIL_VERIFICATION_POPUP';
// ================ Reducer ================ //

const mergeCurrentUser = (oldCurrentUser, newCurrentUser) => {
  const { id: oId, type: oType, attributes: oAttr, ...oldRelationships } = oldCurrentUser || {};
  const { id, type, attributes, ...relationships } = newCurrentUser || {};

  // Passing null will remove currentUser entity.
  // Only relationships are merged.
  // TODO figure out if sparse fields handling needs a better handling.
  return newCurrentUser === null
    ? null
    : oldCurrentUser === null
      ? newCurrentUser
      : { id, type, attributes, ...oldRelationships, ...relationships };
};

const initialState = {
  currentUser: null,
  currentUserShowError: null,
  currentUserHasListings: false,
  currentUserHasListingsError: null,
  currentUserNotificationCount: 0,
  currentUserNotificationCountError: null,
  currentUserHasOrders: null, // This is not fetched unless unverified emails exist
  currentUserHasOrdersError: null,
  sendVerificationEmailInProgress: false,
  sendVerificationEmailError: null,
  updateProfileLanguagesInProgress: false,
  saveShippingAddressInProgress: false,
  saveShippingAddressError: null,
  commissionPercentage: null,
  commissionPercentageError: null,
  deleteAddressInProgress: {
    addressId: null,
    loading: false,
  },
  deleteAddressError: null,
  emailVerifyPopupOpen: false,
};

export default function reducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case CURRENT_USER_SHOW_REQUEST:
      return { ...state, currentUserShowError: null };
    case CURRENT_USER_SHOW_SUCCESS:
      return { ...state, currentUser: mergeCurrentUser(state.currentUser, payload) };
    case CURRENT_USER_SHOW_ERROR:
      // eslint-disable-next-line no-console
      console.error(payload);
      return { ...state, currentUserShowError: payload };

    case CLEAR_CURRENT_USER:
      return {
        ...state,
        currentUser: null,
        currentUserShowError: null,
        currentUserHasListings: false,
        currentUserHasListingsError: null,
        currentUserNotificationCount: 0,
        currentUserNotificationCountError: null,
      };

    case FETCH_CURRENT_USER_HAS_LISTINGS_REQUEST:
      return { ...state, currentUserHasListingsError: null };
    case FETCH_CURRENT_USER_HAS_LISTINGS_SUCCESS:
      return { ...state, currentUserHasListings: payload.hasListings };
    case FETCH_CURRENT_USER_HAS_LISTINGS_ERROR:
      console.error(payload); // eslint-disable-line
      return { ...state, currentUserHasListingsError: payload };

    case FETCH_CURRENT_USER_NOTIFICATIONS_REQUEST:
      return { ...state, currentUserNotificationCountError: null };
    case FETCH_CURRENT_USER_NOTIFICATIONS_SUCCESS:
      return { ...state, currentUserNotificationCount: payload.transactions.length };
    case FETCH_CURRENT_USER_NOTIFICATIONS_ERROR:
      console.error(payload); // eslint-disable-line
      return { ...state, currentUserNotificationCountError: payload };

    case FETCH_CURRENT_USER_HAS_ORDERS_REQUEST:
      return { ...state, currentUserHasOrdersError: null };
    case FETCH_CURRENT_USER_HAS_ORDERS_SUCCESS:
      return { ...state, currentUserHasOrders: payload.hasOrders };
    case FETCH_CURRENT_USER_HAS_ORDERS_ERROR:
      console.error(payload); // eslint-disable-line
      return { ...state, currentUserHasOrdersError: payload };

    case SEND_VERIFICATION_EMAIL_REQUEST:
      return {
        ...state,
        sendVerificationEmailInProgress: true,
        sendVerificationEmailError: null,
      };
    case SEND_VERIFICATION_EMAIL_SUCCESS:
      return {
        ...state,
        sendVerificationEmailInProgress: false,
      };
    case SEND_VERIFICATION_EMAIL_ERROR:
      return {
        ...state,
        sendVerificationEmailInProgress: false,
        sendVerificationEmailError: payload,
      };
    case UPDATE_PROFILE_LANGUAGES_REQUEST:
      return {
        ...state,
        updateProfileLanguagesInProgress: true,
      };
    case UPDATE_PROFILE_LANGUAGES_SUCCESS:
      return {
        ...state,
        updateProfileLanguagesInProgress: false,
      };
    case UPDATE_PROFILE_LANGUAGES_ERROR:
      return {
        ...state,
        updateProfileLanguagesInProgress: false,
      };
    case SAVE_SHIPPING_ADDRESS_REQUEST:
      return {
        ...state,
        saveShippingAddressInProgress: true,
        saveShippingAddressError: null,
      };
    case SAVE_SHIPPING_ADDRESS_SUCCESS:
      return {
        ...state,
        saveShippingAddressInProgress: false,
      };
    case SAVE_SHIPPING_ADDRESS_ERROR:
      return {
        ...state,
        saveShippingAddressInProgress: false,
        saveShippingAddressError: payload,
      };
    case FETCH_COMMISSION_REQUEST:
      return {
        ...state,
        commissionPercentageError: null,
      };
    case FETCH_COMMISSION_SUCCESS:
      return {
        ...state,
        commissionPercentage: payload,
      };
    case FETCH_COMMISSION_ERROR:
      return {
        ...state,
        commissionPercentageError: payload,
      };
    case DELETE_ADDRESS_REQUEST:
      return {
        ...state,
        deleteAddressInProgress: {
          addressId: payload,
          loading: true,
        },
        deleteAddressError: null,
      };
    case DELETE_ADDRESS_SUCCESS:
      return {
        ...state,
        deleteAddressInProgress: {
          addressId: null,
          loading: false,
        },
      };
    case DELETE_ADDRESS_ERROR:
      return {
        ...state,
        deleteAddressInProgress: {
          addressId: null,
          loading: false,
        },
        deleteAddressError: payload,
      };
    case SHOW_EMAIL_VERIFICATION_POPUP:
      return {
        ...state,
        emailVerifyPopupOpen: payload,
      };
    default:
      return state;
  }
}

// ================ Selectors ================ //

export const hasCurrentUserErrors = state => {
  const { user } = state;
  return (
    user.currentUserShowError ||
    user.currentUserHasListingsError ||
    user.currentUserNotificationCountError ||
    user.currentUserHasOrdersError
  );
};

export const verificationSendingInProgress = state => {
  return state.user.sendVerificationEmailInProgress;
};

// ================ Action creators ================ //

export const currentUserShowRequest = () => ({ type: CURRENT_USER_SHOW_REQUEST });

export const currentUserShowSuccess = user => ({
  type: CURRENT_USER_SHOW_SUCCESS,
  payload: user,
});

export const currentUserShowError = e => ({
  type: CURRENT_USER_SHOW_ERROR,
  payload: e,
  error: true,
});

export const clearCurrentUser = () => ({ type: CLEAR_CURRENT_USER });

const fetchCurrentUserHasListingsRequest = () => ({
  type: FETCH_CURRENT_USER_HAS_LISTINGS_REQUEST,
});

export const fetchCurrentUserHasListingsSuccess = hasListings => ({
  type: FETCH_CURRENT_USER_HAS_LISTINGS_SUCCESS,
  payload: { hasListings },
});

const fetchCurrentUserHasListingsError = e => ({
  type: FETCH_CURRENT_USER_HAS_LISTINGS_ERROR,
  error: true,
  payload: e,
});

const fetchCurrentUserNotificationsRequest = () => ({
  type: FETCH_CURRENT_USER_NOTIFICATIONS_REQUEST,
});

export const fetchCurrentUserNotificationsSuccess = transactions => ({
  type: FETCH_CURRENT_USER_NOTIFICATIONS_SUCCESS,
  payload: { transactions },
});

const fetchCurrentUserNotificationsError = e => ({
  type: FETCH_CURRENT_USER_NOTIFICATIONS_ERROR,
  error: true,
  payload: e,
});

const fetchCurrentUserHasOrdersRequest = () => ({
  type: FETCH_CURRENT_USER_HAS_ORDERS_REQUEST,
});

export const fetchCurrentUserHasOrdersSuccess = hasOrders => ({
  type: FETCH_CURRENT_USER_HAS_ORDERS_SUCCESS,
  payload: { hasOrders },
});

const fetchCurrentUserHasOrdersError = e => ({
  type: FETCH_CURRENT_USER_HAS_ORDERS_ERROR,
  error: true,
  payload: e,
});

export const sendVerificationEmailRequest = () => ({
  type: SEND_VERIFICATION_EMAIL_REQUEST,
});

export const sendVerificationEmailSuccess = () => ({
  type: SEND_VERIFICATION_EMAIL_SUCCESS,
});

export const sendVerificationEmailError = e => ({
  type: SEND_VERIFICATION_EMAIL_ERROR,
  error: true,
  payload: e,
});

export const updateProfileLanguagesRequest = () => ({
  type: UPDATE_PROFILE_LANGUAGES_REQUEST,
});

export const updateProfileLanguagesSuccess = () => ({
  type: UPDATE_PROFILE_LANGUAGES_SUCCESS,
});

export const updateProfileLanguagesError = e => ({
  type: UPDATE_PROFILE_LANGUAGES_ERROR,
  error: true,
  payload: e,
});

// ================ Thunks ================ //
export const closeVerificationPopup = state => (dispatch, getState, sdk) => {
  dispatch({ type: SHOW_EMAIL_VERIFICATION_POPUP, payload: state });
  dispatch(fetchCurrentUser());
};
export const saveShippingAddressInProfile = (shippingAddress, viber) => (
  dispatch,
  getState,
  sdk
) => {
  const shippingAddressWithId = shippingAddress?.map((address, index) => {
    return {
      ...address,
      index: index,
    };
  });
  dispatch({ type: SAVE_SHIPPING_ADDRESS_REQUEST });
  return sdk.currentUser
    .updateProfile(
      { publicData: { shippingAddress: shippingAddressWithId, viber } },
      { expand: true }
    )
    .then(response => {
      dispatch({ type: SAVE_SHIPPING_ADDRESS_SUCCESS });
      dispatch(fetchCurrentUser());
      return response;
    })
    .catch(e => {
      dispatch({ type: SAVE_SHIPPING_ADDRESS_ERROR, payload: storableError(e) });
      throw e;
    });
};
export const fetchCommision = () => (dispatch, getState, sdk) => {
  dispatch({ type: FETCH_COMMISSION_REQUEST });
  return sdk
    .assetsByAlias({ paths: ['transactions/commission.json'], alias: 'latest' })
    .then(response => {
      const data = response?.data?.data[0]?.attributes?.data;

      dispatch({ type: FETCH_COMMISSION_SUCCESS, payload: data });
      return response;
    })
    .catch(e => {
      dispatch({ type: FETCH_COMMISSION_ERROR, payload: storableError(e) });
      throw e;
    });
};
const setLanguageCookie = language => {
  saveLanguagePreference(language);
};

export const updateProfileLanguage = language => (dispatch, getState, sdk) => {
  dispatch(updateProfileLanguagesRequest());
  const { currentUser } = getState().user;
  if (!currentUser) {
    setLanguageCookie(language);
    return Promise.resolve();
  }
  return sdk.currentUser
    .updateProfile({ publicData: { language } }, { expand: true })
    .then(response => {
      setLanguageCookie(language);
      dispatch(updateProfileLanguagesSuccess());
      return response;
    })
    .catch(e => {
      console.log(e);
      dispatch(updateProfileLanguagesError(storableError(e)));
      throw e;
    });
};

export const fetchCurrentUserHasListings = () => (dispatch, getState, sdk) => {
  dispatch(fetchCurrentUserHasListingsRequest());
  const { currentUser } = getState().user;

  if (!currentUser) {
    dispatch(fetchCurrentUserHasListingsSuccess(false));
    return Promise.resolve(null);
  }

  const params = {
    // Since we are only interested in if the user has
    // listings, we only need at most one result.
    page: 1,
    perPage: 1,
  };

  return sdk.ownListings
    .query(params)
    .then(response => {
      const hasListings = response.data.data && response.data.data.length > 0;

      const hasPublishedListings =
        hasListings &&
        ensureOwnListing(response.data.data[0]).attributes.state !== LISTING_STATE_DRAFT;
      dispatch(fetchCurrentUserHasListingsSuccess(!!hasPublishedListings));
    })
    .catch(e => dispatch(fetchCurrentUserHasListingsError(storableError(e))));
};

export const fetchCurrentUserHasOrders = () => (dispatch, getState, sdk) => {
  dispatch(fetchCurrentUserHasOrdersRequest());

  if (!getState().user.currentUser) {
    dispatch(fetchCurrentUserHasOrdersSuccess(false));
    return Promise.resolve(null);
  }

  const params = {
    only: 'order',
    page: 1,
    perPage: 1,
  };

  return sdk.transactions
    .query(params)
    .then(response => {
      const hasOrders = response.data.data && response.data.data.length > 0;
      dispatch(fetchCurrentUserHasOrdersSuccess(!!hasOrders));
    })
    .catch(e => dispatch(fetchCurrentUserHasOrdersError(storableError(e))));
};

// Notificaiton page size is max (100 items on page)
const NOTIFICATION_PAGE_SIZE = 100;

export const fetchCurrentUserNotifications = () => (dispatch, getState, sdk) => {
  const transitionsNeedingAttention = getTransitionsNeedingProviderAttention();
  if (transitionsNeedingAttention.length === 0) {
    // Don't update state, if there's no need to draw user's attention after last transitions.
    return;
  }

  const apiQueryParams = {
    only: 'sale',
    last_transitions: transitionsNeedingAttention,
    page: 1,
    perPage: NOTIFICATION_PAGE_SIZE,
  };

  dispatch(fetchCurrentUserNotificationsRequest());
  sdk.transactions
    .query(apiQueryParams)
    .then(response => {
      const transactions = response.data.data;
      dispatch(fetchCurrentUserNotificationsSuccess(transactions));
    })
    .catch(e => dispatch(fetchCurrentUserNotificationsError(storableError(e))));
};

export const fetchCurrentUser = (params = null) => (dispatch, getState, sdk) => {
  dispatch(currentUserShowRequest());
  console.log('first');
  const { isAuthenticated } = getState().auth;
  if (!isAuthenticated) {
    // Make sure current user is null
    dispatch(currentUserShowSuccess(null));
    return Promise.resolve({});
  }

  const parameters = params || {
    include: ['profileImage', 'stripeAccount'],
    'fields.image': [
      'variants.square-small',
      'variants.square-small2x',
      'variants.square-xsmall',
      'variants.square-xsmall2x',
    ],
    'imageVariant.square-xsmall': sdkUtil.objectQueryString({
      w: 40,
      h: 40,
      fit: 'crop',
    }),
    'imageVariant.square-xsmall2x': sdkUtil.objectQueryString({
      w: 80,
      h: 80,
      fit: 'crop',
    }),
  };

  return sdk.currentUser
    .show(parameters)
    .then(response => {
      // const languageInProfile = response?.data?.data?.attributes?.profile?.publicData?.language;

      // const languageInCookie =
      //   document.cookie.match('(^|;)\\s*' + 'language' + '\\s*=\\s*([^;]+)')?.pop() || 'el';
      // if (languageInProfile && languageInProfile != languageInCookie) {
      //   document.cookie = 'language=' + languageInProfile;
      //   window.location.reload(true);
      // }

      const entities = denormalisedResponseEntities(response);
      if (entities.length !== 1) {
        throw new Error('Expected a resource in the sdk.currentUser.show response');
      }
      const currentUser = entities[0];
      restoreProfileLanguage(currentUser?.attributes?.profile?.publicData?.language);

      // Save stripeAccount to store.stripe.stripeAccount if it exists
      if (currentUser.stripeAccount) {
        dispatch(stripeAccountCreateSuccess(currentUser.stripeAccount));
      }

      // set current user id to the logger
      log.setUserId(currentUser.id.uuid);
      dispatch(currentUserShowSuccess(currentUser));
      return currentUser;
    })
    .then(currentUser => {
      dispatch(fetchCurrentUserHasListings());
      dispatch(fetchCurrentUserNotifications());
      if (!currentUser.attributes.emailVerified) {
        dispatch(fetchCurrentUserHasOrders());
      }

      // Make sure auth info is up to date
      dispatch(authInfo());
      return currentUser;
    })
    .catch(e => {
      // Make sure auth info is up to date
      dispatch(authInfo());
      log.error(e, 'fetch-current-user-failed');
      dispatch(currentUserShowError(storableError(e)));
    });
};
export const deleteAddressFromProfile = addressId => (dispatch, getState, sdk) => {
  dispatch({ type: DELETE_ADDRESS_REQUEST, payload: addressId });
  const currentUser = getState().user.currentUser;
  if (!currentUser) {
    return Promise.resolve(null);
  }
  const addresses = currentUser.attributes.profile?.publicData?.shippingAddress || [];
  const addNewAddress = addresses
    .filter((_, idx) => idx !== addressId)
    .map((addr, idx) => ({ ...addr, index: idx }));
  return sdk.currentUser
    .updateProfile({ publicData: { shippingAddress: addNewAddress } }, { expand: true })
    .then(response => {
      dispatch({ type: DELETE_ADDRESS_SUCCESS });
      dispatch(fetchCurrentUser());
      return response;
    })
    .catch(e => {
      dispatch({ type: DELETE_ADDRESS_ERROR, payload: storableError(e) });
      throw e;
    });
};
export const sendVerificationEmail = () => (dispatch, getState, sdk) => {
  if (verificationSendingInProgress(getState())) {
    return Promise.reject(new Error('Verification email sending already in progress'));
  }
  dispatch(sendVerificationEmailRequest());
  return sdk.currentUser
    .sendVerificationEmail()
    .then(() => dispatch(sendVerificationEmailSuccess()))
    .catch(e => dispatch(sendVerificationEmailError(storableError(e))));
};
